const configuredOrigins = [
  process.env.VUE_APP_GHL_PARENT_ORIGIN,
  ...(process.env.VUE_APP_GHL_PARENT_ORIGINS || '').split(','),
].filter(Boolean);

const allowedOrigins = new Set(
  (configuredOrigins.length
    ? configuredOrigins
    : [
        'https://app.gohighlevel.com',
        'https://app.leadconnectorhq.com',
        // GHL payment links embed the provider inside this parent origin.
        'https://link.fastpaydirect.com',
      ])
    .map((origin) => origin.trim().replace(/\/$/, ''))
    .filter((origin) => /^https:\/\//.test(origin))
);

export function getGhlParentOrigin({ preferPaymentLink = false } = {}) {
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      const referrerOrigin = `${referrer.protocol}//${referrer.host}`;
      if (allowedOrigins.has(referrerOrigin)) return referrerOrigin;
    } catch {
      // Fall back to the configured first origin when the referrer is invalid.
    }
  }
  const origins = [...allowedOrigins];
  if (preferPaymentLink) {
    const paymentLinkOrigin = origins.find((origin) => origin === 'https://link.fastpaydirect.com');
    if (paymentLinkOrigin) return paymentLinkOrigin;
  }
  return origins[0];
}

function getDocumentOrigin() {
  return typeof window !== 'undefined' && window.location
    ? window.location.origin
    : '';
}

export function getWindowTargetOrigin(target) {
  if (target) {
    try {
      const targetOrigin = target.location?.origin;
      if (targetOrigin === getDocumentOrigin() || allowedOrigins.has(targetOrigin)) {
        return targetOrigin;
      }
    } catch {
      // Cross-origin opener location is intentionally unreadable.
    }
  }

  return getGhlParentOrigin({ preferPaymentLink: true }) || getDocumentOrigin();
}

// --- R3 Ticket 05: switchable messaging strategy ---------------------------
//
// One env var drives both the send strategy and the origin-validation strategy
// together (plan.md's Phase B rationale: flip a single flag, not several
// independent ones, to keep the blast radius of a re-run of the 2025-09
// failure as small as possible). Reading it inside the function (not caching
// it into a module-level const like `allowedOrigins` above) makes no
// difference in the real build — Vue CLI inlines VUE_APP_* at build time
// either way — but lets tests flip it per-case with vi.stubEnv.
//
// Any value other than the exact opt-in string below — unset, empty, 'legacy',
// or an unrecognized/mistyped value — resolves to 'legacy', which is
// byte-for-byte the pre-Ticket-05 production behavior. That also doubles as
// the emergency rollback: setting VUE_APP_GHL_ORIGIN_STRATEGY back to
// anything other than 'per-location' restores it on the next build. Because
// Vue CLI inlines VUE_APP_* at build time (see the comment above), this is a
// same-commit rebuild+redeploy, not a zero-deploy runtime flip — still far
// faster/safer than reverting a commit, but not literally instant.
const PER_LOCATION_STRATEGY = 'per-location';

function getMessagingStrategy() {
  return process.env.VUE_APP_GHL_ORIGIN_STRATEGY === PER_LOCATION_STRATEGY
    ? PER_LOCATION_STRATEGY
    : 'legacy';
}

// locationId -> Set<origin>, populated from Ticket 04's read API
// (GET /api/newebpay/trusted-origins). Kept as module state so the
// synchronous isTrustedGhlMessage() call sites (every message handler across
// the five GHL iframe components) never need to await a network round trip.
const trustedOriginsByLocation = new Map();
const trustedOriginsLoadInFlight = new Set();

// A real embed only ever needs its own locationId tracked. This bounds how
// many distinct (attacker-suppliable, since postMessage doesn't gate on
// origin before this check runs) locationIds can trigger a background fetch
// against our own backend in a single page session.
const MAX_TRACKED_LOCATIONS = 10;

function normalizeOrigin(origin) {
  return String(origin || '').replace(/\/$/, '');
}

export function setTrustedOriginsForLocation(locationId, origins) {
  if (!locationId) return;
  trustedOriginsByLocation.set(
    locationId,
    new Set(
      (origins || [])
        .map(normalizeOrigin)
        .filter((origin) => /^https:\/\//.test(origin))
    )
  );
}

// Wired to Ticket 04's read API. Best-effort: a failed fetch just leaves the
// location's trust set at "fixed GHL domains only" until the next message
// for that locationId retries the warm-up.
export async function refreshTrustedOriginsForLocation(locationId) {
  if (!locationId) return;
  try {
    const response = await fetch(`/api/newebpay/trusted-origins?locationId=${encodeURIComponent(locationId)}`);
    if (!response.ok) {
      console.error('[ghlMessaging] Failed to load trusted origins:', response.status);
      return;
    }
    const result = await response.json();
    setTrustedOriginsForLocation(locationId, result.trustedOrigins);
  } catch (err) {
    console.error('[ghlMessaging] Failed to load trusted origins:', err);
  }
}

function extractLocationId(event) {
  const raw = event?.data;
  const data = typeof raw === 'string'
    ? (() => {
        try { return JSON.parse(raw); } catch { return null; }
      })()
    : raw;
  return (
    data?.locationId || data?.location_id ||
    data?.data?.locationId || data?.data?.location_id ||
    null
  );
}

// New origin-validation path (DoD A-5): "GHL fixed domains ∪ this location's
// confirmed checkout domains", instead of the legacy own-origin/parent-source
// fallback. This is a replacement strategy, not an additional layer — see the
// 'does not layer on top of the legacy own-origin/parent-source check' test.
function isTrustedByPerLocationOrigin(event) {
  const origin = normalizeOrigin(event.origin);
  if (allowedOrigins.has(origin)) return true;

  const locationId = extractLocationId(event);
  if (!locationId) return false;

  const cached = trustedOriginsByLocation.get(locationId);
  if (cached) return cached.has(origin);

  const trackedLocations = trustedOriginsByLocation.size + trustedOriginsLoadInFlight.size;
  if (!trustedOriginsLoadInFlight.has(locationId) && trackedLocations < MAX_TRACKED_LOCATIONS) {
    trustedOriginsLoadInFlight.add(locationId);
    refreshTrustedOriginsForLocation(locationId).finally(() => {
      trustedOriginsLoadInFlight.delete(locationId);
    });
  }
  return false;
}

export function isTrustedGhlMessage(event) {
  if (!event) return false;

  if (getMessagingStrategy() === PER_LOCATION_STRATEGY) {
    return isTrustedByPerLocationOrigin(event);
  }

  return Boolean(
    normalizeOrigin(event.origin) === getDocumentOrigin() ||
    (
      event.source === window.parent &&
      window.parent !== window
    )
  );
}

export function postToGhl(target, payload) {
  if (!target || target === window) return;
  // Keep compatibility with the last known working GHL checkout integration:
  // it accepted both structured and JSON-string postMessage payloads.
  if (getMessagingStrategy() === PER_LOCATION_STRATEGY) {
    // Real-sandbox evidence (R3 Phase B DoD 10) shows GHL's own frontend only
    // parses the JSON.stringify form — the object form is silently dropped.
    target.postMessage(JSON.stringify(payload), '*');
    return;
  }
  target.postMessage(payload, '*');
  target.postMessage(JSON.stringify(payload), '*');
}

// Single-send variant of postToGhl for AddPaymentMethod.vue's custom_provider_ready /
// custom_element_success_response / custom_element_error_response only: real-transaction
// testing (specs/002-ghl-compliance/tasks.md:47, commit 61bde7e) found that switching these
// three events to postToGhl's dual-send broke the GHL card-on-file return path — do not
// converge them onto postToGhl without new evidence from a real GHL sandbox.
export function postToGhlSingle(target, payload) {
  if (!target || target === window) return;
  target.postMessage(payload, '*');
}

export function postToWindow(target, payload, targetOrigin = getWindowTargetOrigin(target)) {
  if (!target || target === window) return;
  target.postMessage(payload, targetOrigin);
}

// R3 Ticket 06: iframe handshake timing. Extracted from NewebPayCheckout.vue's
// mounted() "send ready immediately, resend every 500ms until acknowledged or the
// attempt cap is hit" loop so its boundary behavior can be tested without mounting
// the Vue component (this repo has no @vue/test-utils/jsdom — see plan.md's stated
// non-goal). Behavior is unchanged: this is the same algorithm, just testable.
//
// Attempt counting is deliberately NOT owned by this loop — it's the caller's
// `hasReachedMaxAttempts()`. NewebPayCheckout.vue's `sendReady` can also be invoked
// from outside this loop (a `window` "focus" listener resends once on focus, and a
// failed `postToGhl` call intentionally does not increment the component's own
// counter). A local counter here would drift from that single source of truth —
// review-via-codex caught exactly this drift in an earlier version of this function.
export function startReadyResendLoop({
  send,
  isAcknowledged,
  hasReachedMaxAttempts,
  intervalMs = 500,
  onAcknowledgedStop,
  onGiveUp,
} = {}) {
  send();

  const timer = setInterval(() => {
    if (isAcknowledged()) {
      clearInterval(timer);
      if (onAcknowledgedStop) onAcknowledgedStop();
      return;
    }
    if (hasReachedMaxAttempts()) {
      clearInterval(timer);
      if (onGiveUp) onGiveUp();
      return;
    }
    send();
  }, intervalMs);

  return {
    stop: () => clearInterval(timer),
  };
}
